'use client';

import * as React from 'react';
import { cn } from '@/shared/utils/styling';
import EyeClosed from '@/shared/assets/eye-closed.svg?react';
import EyeOpen from '@/shared/assets/eye-open.svg?react';
import ClearIcon from '@/shared/assets/clear-icon.svg?react';
import { tv } from 'tailwind-variants';
import { useAutofillDetection } from '@/shared/lib';

const inputVariants = tv({
  base: [
    'flex w-full rounded-[1.6rem] px-[1.6rem] p3 text-foreground bg-muted aria-invalid:bg-light-error h-[6rem]',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'focus:outline-none hover:bg-stroke',
  ],
  variants: {
    shouldFloat: {
      true: 'pt-[2.4rem] pb-[0.9rem]',
      false: 'py-[1.8rem]',
    },
    hasIcon: {
      true: 'pl-[4.2rem]',
      false: 'px-[1.6rem]',
    },
    hasLeftContent: {
      true: 'pl-[4.2rem]',
      false: '',
    },
  },
});

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  showClearButton?: boolean;
  onClear?: () => void;
  label?: string;
  showLabel?: boolean;
  icon?: React.ReactNode;
  showPasswordToggle?: boolean;
  rightContent?: React.ReactNode;
  leftContent?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      showClearButton = true,
      onClear,
      value,
      onChange,
      label,
      placeholder,
      showLabel = true,
      icon,
      showPasswordToggle = true,
      rightContent,
      leftContent,
      ...props
    },
    ref
  ) => {
    const isControlled = value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = React.useState('');
    const [isFocused, setIsFocused] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [rightPadding, setRightPadding] = React.useState(0);
    const [leftPadding, setLeftPadding] = React.useState(0);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const {
      hasAutoFill,
      onAnimationStart,
      reset: resetAutofill,
    } = useAutofillDetection();

    const renderedValue = React.useMemo(() => {
      const v = isControlled ? value : uncontrolledValue;
      if (v === null || v === undefined) return '';
      return typeof v === 'string' ? v : String(v);
    }, [isControlled, uncontrolledValue, value]);

    // Use a more reliable focus detection with proper timing
    React.useEffect(() => {
      const checkFocus = () => {
        if (inputRef.current) {
          setIsFocused(document.activeElement === inputRef.current);
        }
      };

      // Check focus on mount
      checkFocus();

      // Listen for focus changes on the document with proper timing
      const handleFocusIn = () => {
        setTimeout(checkFocus, 0);
      };

      const handleFocusOut = () => {
        setTimeout(checkFocus, 0);
      };

      document.addEventListener('focusin', handleFocusIn);
      document.addEventListener('focusout', handleFocusOut);

      return () => {
        document.removeEventListener('focusin', handleFocusIn);
        document.removeEventListener('focusout', handleFocusOut);
      };
    }, []);

    const handleInputChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isControlled) {
          setUncontrolledValue(e.target.value);
        }
        onChange?.(e);
      },
      [isControlled, onChange]
    );

    const handleRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref && node) {
          (ref as React.MutableRefObject<HTMLInputElement>).current = node;
        }
        if (node) {
          inputRef.current = node;
        }
      },
      [ref]
    );

    const handleClear = React.useCallback(() => {
      if (!isControlled) {
        setUncontrolledValue('');
      }
      onClear?.();
      // Create a synthetic event for onChange
      const syntheticEvent = {
        target: { value: '' },
        currentTarget: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange?.(syntheticEvent);
    }, [isControlled, onChange, onClear]);

    const handleFocus = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        props.onFocus?.(e);
        resetAutofill();
      },
      [props]
    );

    const handleBlur = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        props.onBlur?.(e);
        resetAutofill();
      },
      [props]
    );

    const togglePasswordVisibility = React.useCallback(() => {
      setShowPassword(prev => !prev);
    }, []);

    // Determine the actual input type
    const actualType = type === 'password' && showPassword ? 'text' : type;

    const shouldFloat =
      showLabel && (isFocused || renderedValue.length > 0 || hasAutoFill);
    const labelText = label || placeholder;

    // Calculate right padding based on buttons
    const hasPasswordToggle =
      type === 'password' && showPasswordToggle && isFocused;
    const hasClearButton = showClearButton && isFocused;

    const calculateRightPadding = React.useCallback(() => {
      if (!inputRef.current) return 0;

      const rightContainer = inputRef.current.parentElement?.querySelector(
        '[data-right-container]'
      );
      if (!rightContainer) return 0;

      const containerWidth = rightContainer.getBoundingClientRect().width;

      return containerWidth + 24;
    }, [rightContent, hasPasswordToggle, hasClearButton]);

    const calculateLeftPadding = React.useCallback(() => {
      if (!inputRef.current) return 0;

      const leftContainer = inputRef.current.parentElement?.querySelector(
        '[data-left-container]'
      );
      if (!leftContainer) return 0;

      const containerWidth = leftContainer.getBoundingClientRect().width;

      return containerWidth + 12; // 0.8rem = 12px
    }, [leftContent]);

    React.useEffect(() => {
      setRightPadding(calculateRightPadding());
    }, [
      calculateRightPadding,
      rightContent,
      hasPasswordToggle,
      hasClearButton,
    ]);

    React.useEffect(() => {
      setLeftPadding(calculateLeftPadding());
    }, [calculateLeftPadding, leftContent]);

    const hasLeftContent = !!leftContent;

    const inputClasses = inputVariants({
      shouldFloat,
      hasIcon: !!icon && !leftContent,
      hasLeftContent: hasLeftContent && !icon,
    });

    return (
      <div className="relative w-full">
        {showLabel && labelText && (
          <label
            className={cn(
              'absolute left-6 transition-all duration-200 ease-in-out pointer-events-none',
              'transition-all duration-200 ease-in-out pointer-events-none p3 top-1/2 -translate-y-1/2 text-base text-muted-foreground',
              shouldFloat && 'top-[9px] translate-none p6'
            )}>
            {labelText}
          </label>
        )}

        {icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            {icon}
          </div>
        )}

        {leftContent && (
          <div
            className={cn(
              'absolute top-0 left-[2rem] h-full flex items-center text-secondary',
              leftContent ? 'left-[0.8rem]' : ''
            )}
            data-left-container>
            {leftContent}
          </div>
        )}

        <input
          type={actualType}
          className={cn(inputClasses, className)}
          style={{
            paddingRight: `${rightPadding}px`,
            paddingLeft:
              hasLeftContent && !icon ? `${leftPadding}px` : undefined,
          }}
          ref={handleRef}
          value={renderedValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onAnimationStart={onAnimationStart}
          placeholder={shouldFloat ? placeholder : ''}
          {...props}
        />

        <div
          className="absolute top-0 right-[0.8rem] h-full flex items-center text-secondary"
          data-right-container>
          {hasClearButton && (
            <div
              onMouseDown={e => {
                // Prevent input from losing focus
                e.preventDefault();
              }}
              onClick={handleClear}
              className="cursor-pointer w-[4.8rem] h-[4.8rem] flex items-center justify-center">
              <ClearIcon className="size-10" />
            </div>
          )}
          {hasPasswordToggle && (
            <div
              onMouseDown={e => {
                // Prevent input from losing focus
                e.preventDefault();
              }}
              onClick={togglePasswordVisibility}
              className="cursor-pointer w-[4.8rem] h-[4.8rem] flex items-center justify-center">
              {showPassword ? (
                <EyeOpen className="size-10" />
              ) : (
                <EyeClosed className="size-10" />
              )}
            </div>
          )}
          {rightContent}
        </div>
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
